import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiService } from '../services/api';

export interface FeatureFlag {
  name: string;
  enabled: boolean;
  description: string;
  rolloutPercentage?: number;
}

export interface AppConfig {
  app: {
    name: string;
    version: string;
    environment: 'development' | 'production' | 'test';
    port: number;
    debug: boolean;
  };
  features: FeatureFlag[];
}

interface ConfigContextType {
  config: AppConfig | null;
  loading: boolean;
  error: string | null;
  isFeatureEnabled: (featureName: string) => boolean;
  refreshConfig: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // If unauthenticated, provide sensible defaults and avoid calling private endpoints
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      if (!token) {
        setConfig({
          app: {
            name: 'PROBOXLB',
            version: '1.0.0',
            environment: 'development',
            port: 3001,
            debug: false,
          },
          features: [],
        });
        return;
      }

      const [configRes, featuresRes] = await Promise.all([
        apiService.get('/settings/config').catch(() => ({ data: { data: { app: {} } } })),
        apiService.get('/settings/features').catch(() => ({ data: { data: [] } })),
      ]);

      const appConfig = configRes.data?.data?.app || {};
      const features = featuresRes.data?.data || [];

      setConfig({
        app: {
          name: appConfig.name || 'PROBOXLB',
          version: appConfig.version || '1.0.0',
          environment: appConfig.environment || 'development',
          port: appConfig.port || 3001,
          debug: appConfig.debug || false,
        },
        features,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load configuration';
      setError(message);
      console.error('Config loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const isFeatureEnabled = (featureName: string): boolean => {
    if (!config) return false;
    const feature = config.features.find((f) => f.name === featureName);
    return feature?.enabled ?? false;
  };

  const refreshConfig = async (): Promise<void> => {
    await loadConfig();
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const value: ConfigContextType = {
    config,
    loading,
    error,
    isFeatureEnabled,
    refreshConfig,
  };

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

